import { z } from 'zod'

// ISSUE #19 / #20: Learning Extractor が抽出する候補の種別。
//
// `memories.kind` (MemoryKindSchema) とは別 enum。理由:
//   - `skill_candidate` は memories テーブルには insert されない (Phase 6 で
//     Skill Registry へ振り分ける目印として扱う)。
//   - Phase 3 のスコープでは `tool_usage_note` / `environment_fact` /
//     `temporary_context` / `deprecated_fact` を抽出対象から外し、agent 自走で
//     生成しやすい 6 種 + skill_candidate に限定する。
//
// memories へ insert する候補と、Skill Registry へ流す `skill_candidate` を
// Learning Extractor 側でルーティング分岐するため、enum を独立させる。
export const ExtractedCandidateKindSchema = z.enum([
  'user_preference',
  'project_fact',
  'decision',
  'workflow_rule',
  'failure_pattern',
  'security_caution',
  'skill_candidate',
])
export type ExtractedCandidateKind = z.infer<typeof ExtractedCandidateKindSchema>

// Learning Extractor が出力する候補 1 件。Worker `services/memories.ts` の
// `insertMemoryCandidate` に渡す前に kind === 'skill_candidate' の場合は
// Skill Registry 側 (Phase 6) へ流すために分岐する。
//
// §11.3 で Claude に要求する 7 フィールド (kind / summary / source_evidence /
// confidence / risk_level / requires_approval / reason) を全て optional 込みで
// 受け入れる。`source_evidence` 等が欠けていても破棄せず、後段の
// `normalizeCandidate` で `source` への fallback や log 出力に使う
// (`confidence < threshold` を後で filter する用途も残す)。
//
// 必須フィールドは `kind` と `summary` のみ。`source` は extractor 側で
// `source_evidence` / `source` / `job:<id>` の優先順で補完する。
export const ExtractedCandidateSchema = z.object({
  kind: ExtractedCandidateKindSchema,
  summary: z.string().min(1).max(2000),

  // ---- §11.3 で LLM に要求する 7 フィールド (kind / summary は上記) ----
  // LLM 出力では存在する想定。ない場合は extractor 側で `source` へ fallback。
  source_evidence: z.string().min(1).max(2000).optional(),
  // confidence は 0-1。閾値 filter (e.g. < 0.3 で破棄) の余地を残しておく。
  confidence: z.number().min(0).max(1).optional(),
  risk_level: z.enum(['low', 'medium', 'high']).optional(),
  requires_approval: z.boolean().optional(),
  reason: z.string().min(1).max(2000).optional(),

  // ---- insertMemoryCandidate への架け橋 ----
  // ソース (例: `job:<id>` / `slack:<channel>:<ts>`)。memories.source に入る。
  // LLM 出力には基本含まれないため optional。extractor 側で必ず埋める。
  source: z.string().min(1).max(255).optional(),
  // 抽出元の raw evidence (Layer 0) への R2 key。attestation_chain の
  // `raw_capture` step とリンクする。
  raw_evidence_r2_key: z.string().min(1).max(512).optional(),
  project: z.string().min(1).max(255).optional(),
  source_uri: z.string().url().optional(),
  // §6.4 のスコア default を Extractor 側で上書きしたい場合の override。
  trust_score: z.number().min(0).max(1).optional(),
  utility_score: z.number().min(0).max(1).optional(),
  safety_score: z.number().min(0).max(1).optional(),
  source_reliability: z.number().min(0).max(1).optional(),
  stale_after: z.string().optional(),
})
export type ExtractedCandidate = z.infer<typeof ExtractedCandidateSchema>
